import React, { useEffect, useState } from "react";
import { Box, Button, Stack, TextField, useTheme } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import Popup from "../../../components/Popup";
import { FormattedMessage, useIntl } from "react-intl";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import AxionControls from "../../../components/controls/AxionControls";
import { updateLaborHour } from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import { toDayjsLocale } from "../../cmms/cmmsDateLocale";
import { asUtcIso } from "../../cmms/cmmsFormat";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getOutlinedButtonSx,
} from "../../cmms/assetHierarchyStyles";

// asUtcIso on the way in: the API sends UTC without a trailing Z, so a bare dayjs(value) would
// read it as local and show the picker a time two hours behind what was saved.
const toDayjs = (value) => (value ? dayjs(asUtcIso(value)) : null);
// ...and on the way out too, for the same reason. The load effect seeds state with the raw API
// string, and onChange only replaces it with a Dayjs if the user actually touches the picker.
// Saving an untouched field would otherwise re-read that naive string as local and write the
// instant back shifted by the local offset — every edit-save drifting it one offset further.
const toIso = (value) => {
  if (!value) return null;
  const d = dayjs(asUtcIso(value));
  return d.isValid() ? d.toISOString() : null;
};

export default function LaborHourEditDialog({ open, onClose, workOrderId, laborHour, companyKey, onSuccess }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const dateLocale = toDayjsLocale(intl.locale);
  const [checkInAt, setCheckInAt] = useState(null);
  const [checkOutAt, setCheckOutAt] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (laborHour) {
      setCheckInAt(laborHour.checkInAt ?? null);
      setCheckOutAt(laborHour.checkOutAt ?? null);
      setNotes(laborHour.notes ?? "");
      setError(null);
    }
  }, [open, laborHour]);

  const handleSubmit = async () => {
    setError(null);
    if (!checkInAt) {
      setError(intl.formatMessage(messages.laborCheckInRequiredError));
      return;
    }
    // toDayjs, not a bare dayjs: one side is often a Dayjs the user just picked while the other is
    // still the naive API string, and comparing those directly reads the untouched side as local —
    // letting a check-out that really precedes check-in slip past this guard.
    if (checkOutAt && toDayjs(checkOutAt).isBefore(toDayjs(checkInAt))) {
      setError(intl.formatMessage(messages.laborCheckOutAfterCheckIn));
      return;
    }
    setSubmitting(true);
    try {
      await updateLaborHour(workOrderId, laborHour.id, {
        companyKey,
        checkInAt: toIso(checkInAt),
        checkOutAt: toIso(checkOutAt),
        notes: notes?.trim() || null,
      });
      pushNotification(intl.formatMessage(messages.toastLaborUpdated));
      onSuccess?.();
      onClose?.();
    } catch (err) {
      pushErrorMessage(formatCmmsError(err, intl.formatMessage(messages.laborSaveFailed)));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(messages.laborEditTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dateLocale}>
          <DateTimePicker
            label={intl.formatMessage(messages.laborCheckInRequired)}
            value={toDayjs(checkInAt)}
            onChange={(value) => setCheckInAt(value)}
            renderInput={(params) => <TextField fullWidth size="small" {...params} />}
          />
          <DateTimePicker
            label={intl.formatMessage(messages.laborCheckOut)}
            value={toDayjs(checkOutAt)}
            onChange={(value) => setCheckOutAt(value)}
            renderInput={(params) => <TextField fullWidth size="small" {...params} />}
          />
        </LocalizationProvider>
        <TextField
          label={intl.formatMessage(messages.fieldNotes)}
          fullWidth
          multiline
          minRows={2}
          size="small"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        {error &&
          <Box sx={{ color: "error.main", fontSize: 13 }}>{error}</Box>
        }
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}><FormattedMessage {...cmmsMessages.cancel} /></Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}><FormattedMessage {...cmmsMessages.save} /></Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
